// ====================================================================
// ADMIN PAGE — /admin
// --------------------------------------------------------------------
// Simple passphrase-protected list of incoming quote requests.
//
// Password: ADMIN_TOKEN in /app/backend/.env (current default:
//   woodstone-admin-2026 — change it before going live!).
//
// After you deploy, visit  https://<your-domain>/admin  and enter the
// token above to see everything submitted through the contact form.
// ====================================================================

import { useEffect, useState } from "react";
import axios from "axios";
import {
  Lock,
  RefreshCw,
  LogOut,
  Download,
  Mail,
  Phone,
  MapPin,
  Image as ImageIcon,
  X,
  Eye,
  Calculator as CalcIcon,
  UserPlus,
  FileText,
} from "lucide-react";
import useSEO from "../hooks/useSEO";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const STORAGE_KEY = "woodstone_admin_token";

function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-AU", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

function csvEscape(v) {
  if (v == null) return "";
  const s = String(v).replace(/"/g, '""');
  return /[",\n]/.test(s) ? `"${s}"` : s;
}

function downloadCsv(rows) {
  const cols = [
    "created_at",
    "name",
    "phone",
    "email",
    "suburb",
    "service",
    "has_photo",
    "message",
  ];
  const header = cols.join(",");
  const body = rows.map((r) => cols.map((c) => csvEscape(r[c])).join(","));
  const blob = new Blob([header + "\n" + body.join("\n")], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `woodstone-quotes-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function StatCard({ icon: Icon, label, counts, testid }) {
  const c = counts || { today: 0, week: 0, month: 0, all: 0 };
  return (
    <div
      className="bg-white border border-[#DCD7CE] rounded-2xl p-5"
      data-testid={testid}
    >
      <div className="flex items-center gap-2 text-[#4A4D4A]">
        <Icon size={15} className="text-[#7A2D2F]" />
        <span className="text-[12px] uppercase tracking-[0.18em] font-semibold">
          {label}
        </span>
      </div>
      <div
        className="mt-3 text-3xl text-[#1A1A1A] tabular-nums"
        style={{ fontFamily: "'Fraunces', serif" }}
      >
        {c.all}
      </div>
      <div className="mt-1 text-[12px] text-[#4A4D4A] flex items-center gap-3 flex-wrap">
        <span>
          <span className="font-semibold text-[#1A1A1A]">{c.today}</span> today
        </span>
        <span>·</span>
        <span>
          <span className="font-semibold text-[#1A1A1A]">{c.week}</span> /7d
        </span>
        <span>·</span>
        <span>
          <span className="font-semibold text-[#1A1A1A]">{c.month}</span> /30d
        </span>
      </div>
    </div>
  );
}

export default function AdminPage() {
  useSEO({
    title: "Admin — Woodstone Construction",
    description: "Internal admin dashboard.",
  });

  const [token, setToken] = useState(() => localStorage.getItem(STORAGE_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [stats, setStats] = useState(null);

  async function verify(t = token) {
    setError("");
    setLoading(true);
    try {
      await axios.post(
        `${API}/admin/verify`,
        {},
        { headers: { "X-Admin-Token": t } }
      );
      localStorage.setItem(STORAGE_KEY, t);
      setAuthed(true);
      await Promise.all([loadRows(t), loadStats(t)]);
    } catch (err) {
      const msg =
        err?.response?.status === 401
          ? "That token doesn’t match. Try again."
          : "Couldn’t reach the server. Try again in a moment.";
      setError(msg);
      setAuthed(false);
      localStorage.removeItem(STORAGE_KEY);
    } finally {
      setLoading(false);
    }
  }

  async function loadStats(t = token) {
    try {
      const res = await axios.get(`${API}/admin/stats`, {
        headers: { "X-Admin-Token": t },
      });
      setStats(res.data);
    } catch {
      /* non-fatal */
    }
  }

  async function loadRows(t = token) {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/quotes`, {
        headers: { "X-Admin-Token": t },
        params: { limit: 500 },
      });
      setRows(res.data || []);
    } catch (err) {
      setError("Couldn’t load submissions.");
      if (err?.response?.status === 401) {
        setAuthed(false);
        localStorage.removeItem(STORAGE_KEY);
      }
    } finally {
      setLoading(false);
    }
  }

  async function openPhoto(id) {
    setPhotoPreview({ loading: true });
    try {
      const res = await axios.get(`${API}/quotes/${id}/photo`, {
        headers: { "X-Admin-Token": token },
      });
      setPhotoPreview({
        src: res.data?.photo_base64,
        name: res.data?.photo_name,
      });
    } catch {
      setPhotoPreview(null);
    }
  }

  useEffect(() => {
    if (token) verify(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function logout() {
    localStorage.removeItem(STORAGE_KEY);
    setToken("");
    setAuthed(false);
    setRows([]);
    setStats(null);
  }

  // =================== LOGIN ===================
  if (!authed) {
    return (
      <main
        className="min-h-[70vh] flex items-center justify-center px-6 py-16"
        data-testid="admin-login"
      >
        <div className="w-full max-w-md bg-white border border-[#DCD7CE] rounded-3xl p-8 shadow-[0_20px_50px_rgba(20,40,31,0.06)]">
          <div className="h-11 w-11 rounded-2xl bg-[#F3E8E5] flex items-center justify-center mb-5">
            <Lock size={18} className="text-[#7A2D2F]" />
          </div>
          <h1
            className="text-3xl text-[#1A1A1A] tracking-tight mb-2"
            style={{ fontFamily: "'Fraunces', serif" }}
          >
            Admin access
          </h1>
          <p className="text-sm text-[#4A4D4A] mb-6">
            Enter your admin token to view incoming quote requests.
          </p>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              verify();
            }}
          >
            <label className="block text-[13px] font-medium text-[#1A1A1A] mb-2">
              Admin token
            </label>
            <input
              type="password"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="••••••••••••••"
              className="w-full bg-[#F8F7F4] border border-[#DCD7CE] rounded-xl px-4 py-3 text-[#1A1A1A] focus:ring-2 focus:ring-[#7A2D2F]/30 focus:border-[#7A2D2F] focus:outline-none"
              data-testid="admin-token-input"
              autoFocus
            />
            {error && (
              <p
                className="mt-3 text-sm text-[#5E2023]"
                data-testid="admin-error"
              >
                {error}
              </p>
            )}
            <button
              type="submit"
              disabled={loading || !token.trim()}
              className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-[#7A2D2F] text-white rounded-full px-6 py-3.5 font-semibold hover:bg-[#5E2023] transition-all disabled:opacity-60"
              data-testid="admin-login-btn"
            >
              {loading ? "Checking…" : "Sign in"}
            </button>
          </form>
          <p className="text-xs text-[#8A8A82] mt-5 leading-relaxed">
            Forgot it? The token lives in your backend <code>.env</code> as{" "}
            <code>ADMIN_TOKEN</code>.
          </p>
        </div>
      </main>
    );
  }

  // =================== DASHBOARD ===================
  return (
    <main
      className="max-w-7xl mx-auto px-6 md:px-12 py-12"
      data-testid="admin-dashboard"
    >
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-[12px] uppercase tracking-[0.25em] text-[#C9A876] font-semibold mb-2">
            Admin
          </div>
          <h1
            className="text-4xl md:text-5xl text-[#1A1A1A] tracking-tight"
            style={{ fontFamily: "'Fraunces', serif" }}
          >
            Quote requests
          </h1>
          <p className="text-[#4A4D4A] mt-1">
            {rows.length} submission{rows.length === 1 ? "" : "s"} · stored
            securely in your database
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => {
              loadRows();
              loadStats();
            }}
            className="inline-flex items-center gap-2 bg-white border border-[#DCD7CE] rounded-full px-4 py-2.5 text-sm font-semibold hover:border-[#7A2D2F] hover:text-[#7A2D2F]"
            data-testid="admin-refresh"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
            Refresh
          </button>
          <button
            onClick={() => downloadCsv(rows)}
            disabled={!rows.length}
            className="inline-flex items-center gap-2 bg-white border border-[#DCD7CE] rounded-full px-4 py-2.5 text-sm font-semibold hover:border-[#7A2D2F] hover:text-[#7A2D2F] disabled:opacity-50"
            data-testid="admin-csv"
          >
            <Download size={14} /> Export CSV
          </button>
          <button
            onClick={logout}
            className="inline-flex items-center gap-2 bg-[#7A2D2F] text-white rounded-full px-4 py-2.5 text-sm font-semibold hover:bg-[#5E2023]"
            data-testid="admin-logout"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </div>

      {error && (
        <div
          className="mb-6 bg-[#F3E8E5] border border-[#DCC6C4] text-[#5E2023] rounded-xl px-4 py-3 text-sm"
          data-testid="admin-error"
        >
          {error}
        </div>
      )}

      {/* ============ STATS ============ */}
      {stats && (
        <div className="mb-10" data-testid="admin-stats">
          <div className="flex items-end justify-between mb-3">
            <h2
              className="text-xl text-[#1A1A1A] tracking-tight"
              style={{ fontFamily: "'Fraunces', serif" }}
            >
              At a glance
            </h2>
            <span className="text-xs text-[#9A9A90]">
              Includes Google Analytics for deeper insights
            </span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            <StatCard
              icon={Eye}
              label="Page views"
              counts={stats.counts?.page_view}
              testid="stat-page-views"
            />
            <StatCard
              icon={CalcIcon}
              label="Calculator uses"
              counts={stats.counts?.calculator_used}
              testid="stat-calc-uses"
            />
            <StatCard
              icon={UserPlus}
              label="Calculator leads"
              counts={stats.counts?.calculator_lead}
              testid="stat-calc-leads"
            />
            <StatCard
              icon={FileText}
              label="Quote requests"
              counts={stats.counts?.quote_request}
              testid="stat-quote-requests"
            />
          </div>

          {Array.isArray(stats.page_views) && stats.page_views.length > 0 && (
            <div className="mt-5 bg-white border border-[#DCD7CE] rounded-2xl p-5">
              <div className="text-[12px] uppercase tracking-[0.2em] text-[#4A4D4A] font-semibold mb-3">
                Most visited pages
              </div>
              <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-2 text-sm">
                {stats.page_views.slice(0, 8).map((p) => (
                  <li
                    key={p.page}
                    className="flex justify-between text-[#1A1A1A]"
                  >
                    <span className="truncate text-[#4A4D4A]">{p.page}</span>
                    <span className="font-semibold ml-2">{p.count}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* ============ LEADS / QUOTES ============ */}
      <div className="mb-3">
        <h2
          className="text-xl text-[#1A1A1A] tracking-tight"
          style={{ fontFamily: "'Fraunces', serif" }}
        >
          Quote submissions
        </h2>
      </div>

      {rows.length === 0 && !loading ? (
        <div className="bg-white border border-dashed border-[#DCD7CE] rounded-3xl p-12 text-center">
          <h3
            className="text-2xl text-[#1A1A1A] mb-2"
            style={{ fontFamily: "'Fraunces', serif" }}
          >
            No quote requests yet.
          </h3>
          <p className="text-[#4A4D4A]">
            New submissions from your contact form will show up here.
          </p>
        </div>
      ) : (
        <div className="bg-white border border-[#DCD7CE] rounded-3xl overflow-hidden shadow-[0_8px_30px_rgba(20,40,31,0.04)]">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-table">
              <thead className="bg-[#F8F7F4] text-[#4A4D4A] text-[12px] uppercase tracking-[0.15em]">
                <tr>
                  <th className="text-left px-5 py-3">Received</th>
                  <th className="text-left px-5 py-3">Name</th>
                  <th className="text-left px-5 py-3">Contact</th>
                  <th className="text-left px-5 py-3">Suburb</th>
                  <th className="text-left px-5 py-3">Service</th>
                  <th className="text-left px-5 py-3">Message</th>
                  <th className="text-left px-5 py-3">Photo</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={r.id}
                    className="border-t border-[#EFECE6] hover:bg-[#F8F7F4]/60"
                    data-testid={`admin-row-${r.id}`}
                  >
                    <td className="px-5 py-4 whitespace-nowrap text-[#4A4D4A]">
                      {formatDate(r.created_at)}
                    </td>
                    <td className="px-5 py-4 font-semibold text-[#1A1A1A]">
                      {r.name}
                    </td>
                    <td className="px-5 py-4">
                      <a
                        href={`tel:${r.phone}`}
                        className="flex items-center gap-1.5 text-[#1A1A1A] hover:text-[#7A2D2F]"
                      >
                        <Phone size={13} /> {r.phone}
                      </a>
                      <a
                        href={`mailto:${r.email}`}
                        className="flex items-center gap-1.5 text-[#4A4D4A] hover:text-[#7A2D2F] break-all"
                      >
                        <Mail size={13} /> {r.email}
                      </a>
                    </td>
                    <td className="px-5 py-4 text-[#4A4D4A]">
                      <span className="inline-flex items-center gap-1">
                        <MapPin size={13} /> {r.suburb || "—"}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <span className="inline-block bg-[#F3E8E5] text-[#7A2D2F] rounded-full px-3 py-1 text-[12px] font-semibold">
                        {r.service}
                      </span>
                    </td>
                    <td className="px-5 py-4 max-w-[280px] text-[#4A4D4A]">
                      <div className="line-clamp-3">{r.message || "—"}</div>
                    </td>
                    <td className="px-5 py-4">
                      {r.has_photo ? (
                        <button
                          onClick={() => openPhoto(r.id)}
                          className="inline-flex items-center gap-1.5 text-[#7A2D2F] font-semibold"
                          data-testid={`admin-photo-${r.id}`}
                        >
                          <ImageIcon size={14} /> View
                        </button>
                      ) : (
                        <span className="text-[#9A9A90]">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* photo modal */}
      {photoPreview && (
        <div
          className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-6"
          onClick={() => setPhotoPreview(null)}
          data-testid="admin-photo-modal"
        >
          <div
            className="bg-white rounded-2xl overflow-hidden max-w-2xl w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-3 border-b border-[#DCD7CE]">
              <div className="font-semibold text-[#1A1A1A] truncate">
                {photoPreview.name || "Photo"}
              </div>
              <button
                onClick={() => setPhotoPreview(null)}
                className="p-1.5 text-[#4A4D4A] hover:text-[#7A2D2F]"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>
            <div className="p-4 bg-[#F8F7F4] flex items-center justify-center">
              {photoPreview.loading ? (
                <div className="py-20 text-[#4A4D4A]">Loading…</div>
              ) : photoPreview.src ? (
                <img
                  src={photoPreview.src}
                  alt="Customer upload"
                  className="max-h-[70vh] rounded-lg"
                />
              ) : (
                <div className="py-20 text-[#4A4D4A]">No photo available.</div>
              )}
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
